import React, { useMemo, useState, useCallback } from "react";
import { FixedSizeList as List } from "react-window";
import { Checkbox, Typography, Box, styled, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { SignatureConfigurationNode } from "entities/ApiModel/SignatureConfigurationNode";
import { colors, fontSize, height, padding, width } from "theme/defaultStyle";

/* ---------------- styled ---------------- */

const RowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ disabled }) => ({
  display: "flex",
  alignItems: "center",
  overflow: "hidden !important",

  height: 36,
  "&:hover": {
    backgroundColor: disabled ? "transparent" : colors.grey10,
  },
}));

const LabelText = styled(Typography)({
  fontSize: fontSize.large,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const iconStyle = {
  color: colors.blue01,
  fontWeight: "bold",
  height: height.xxsmall0,
  width: width.xxsmall0,
};

/* ---------------- helpers ---------------- */

type FlatNode = {
  id: string;
  node: SignatureConfigurationNode;
  depth: number;
};

/* ---------------- component ---------------- */

type Props = {
  data: SignatureConfigurationNode[];
  onDataChanged: (data: SignatureConfigurationNode) => void;
  highlightSelected?: boolean;
  isSignatureConfigurationDisabled?: boolean;
  duplicatedAgencies?: string[];
};

// Collect all IDs of nodes to expand if they or their descendants are selected
const getExpandedFromSelected = (nodes: SignatureConfigurationNode[]): Set<string> => {
  const expandedIds = new Set<string>();

  const walk = (node: SignatureConfigurationNode): boolean => {
    let hasSelected = !!node.Selected;

    if (node.Children?.length) {
      for (const child of node.Children) {
        if (walk(child)) {
          hasSelected = true;
        }
      }

      if (hasSelected) {
        expandedIds.add(node.ID);
      }
    }

    return hasSelected;
  };

  nodes.forEach(walk);
  return expandedIds;
};

export default function RecursiveTreeView({
  data,
  onDataChanged,
  highlightSelected = false,
  isSignatureConfigurationDisabled = false,
  duplicatedAgencies = [],
}: Props) {
  /** expanded nodes */
  const [expanded, setExpanded] = useState<Set<string>>(() => getExpandedFromSelected(data));

  const duplicateSet = useMemo(() => new Set(duplicatedAgencies), [duplicatedAgencies]);

  /** flatten visible tree */
  const flatNodes = useMemo<FlatNode[]>(() => {
    const result: FlatNode[] = [];

    const walk = (node: SignatureConfigurationNode, depth: number) => {
      result.push({ id: node.ID, node, depth });

      if (expanded.has(node.ID) && node.Children?.length) {
        node.Children.forEach((child) => walk(child, depth + 1));
      }
    };

    data.forEach((root) => walk(root, 0));
    return result;
  }, [data, expanded]);

  /** checkbox state (O(1)) */

  const getCheckState = useCallback((node: SignatureConfigurationNode) => {
    if (!node.Children || node.Children.length === 0) {
      return {
        checked: !!node.Selected,
        indeterminate: false,
      };
    }
  
    let total = 0;
    let checkedCount = 0;
  
    const walk = (n: SignatureConfigurationNode) => {
      if (!n.Children || n.Children.length === 0) {
        total += 1;
        if (n.Selected) checkedCount += 1;
      } else {
        n.Children.forEach(walk);
      }
    };
  
    node.Children.forEach(walk);
  
    return {
      checked: checkedCount === total && total > 0,
      indeterminate: checkedCount > 0 && checkedCount < total,
    };
  }, []);
  

  /** cascade select (mutates subtree ONCE) */
  const cascadeSelect = (node: SignatureConfigurationNode, checked: boolean) => {
    if (!node.Children || node.Children.length === 0) {
      node.Selected = checked;
      return;
    }

    node.Children.forEach((child) => cascadeSelect(child, checked));
  };

  /** handlers */
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCheck = (node: SignatureConfigurationNode, checked: boolean) => {
    cascadeSelect(node, checked);

    // Expand all children automatically
    const newExpanded = new Set(expanded);
    const walkExpandChildren = (n: SignatureConfigurationNode) => {
      if (n.Children?.length) {
        newExpanded.add(n.ID);
        n.Children.forEach(walkExpandChildren);
      }
    };
    walkExpandChildren(node);

    // Expand all parent paths
    const findAndExpandParents = (
      nodes: SignatureConfigurationNode[],
      targetId: string,
      path: string[] = [],
    ): boolean => {
      for (let n of nodes) {
        const currentPath = [...path, n.ID];
        if (n.ID === targetId) {
          currentPath.forEach((id) => newExpanded.add(id));
          return true;
        }
        if (n.Children && findAndExpandParents(n.Children, targetId, currentPath)) {
          return true;
        }
      }
      return false;
    };
    findAndExpandParents(data, node.ID);

    setExpanded(newExpanded);
    onDataChanged(data[0]);
  };

  /** row renderer (virtualized) */
  const Row = ({ index, style }: any) => {
    const { node, depth } = flatNodes[index];
    const { checked, indeterminate } = getCheckState(node);
    const hasChildren = !!node.Children?.length;
    const isDuplicated = duplicateSet.has(node.Name);

    return (
      <RowContainer
        style={style}
        disabled={isSignatureConfigurationDisabled}
        sx={{
          pl: depth * 24 + "px",
          backgroundColor: highlightSelected && node.Selected ? "#EFF7FF" : "transparent",
        }}
      >
        {hasChildren ? (
          <Box
            onClick={() => toggleExpand(node.ID)}
            sx={{ width: 24 }}
          >
            {expanded.has(node.ID) ? (
              <ExpandMoreIcon
                fontSize="small"
                sx={iconStyle}
              />
            ) : (
              <ChevronRightIcon
                fontSize="small"
                sx={iconStyle}
              />
            )}
          </Box>
        ) : (
          <Box sx={{ width: 24 }} />
        )}

        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          disabled={isSignatureConfigurationDisabled}
          onChange={(e) => handleCheck(node, e.target.checked)}
          size="small"
          sx={{ p: padding.small1 }}
        />

        <Tooltip title={node.Name} placement="top" arrow>
        <LabelText
          sx={{
            color: isDuplicated ? colors.red01 : "inherit",
          }}
        >
          {isDuplicated ? `* ${node.Name}` : node.Name}
        </LabelText>
        </Tooltip>
      </RowContainer>
    );
  };
  return (
    <List
      height={600}
      itemCount={flatNodes.length}
      itemSize={36}
      width="100%"
    >
      {Row}
    </List>
  );
}
